/**
 * Module 6D — Super-admin auth provider.
 *
 * STRUCTURALLY SEPARATE from the studio `AuthProvider`. Each lives in its
 * own React context, reads its own localStorage key, and calls its own
 * `/api/admin/auth/*` endpoints. A studio session in `ls.auth.token` is
 * invisible to this provider; a leaked studio session cannot grant admin
 * access here. See `auth.require_super_admin` for the matching backend
 * separation guarantee.
 *
 * The `/admin/login` page mints an admin session via the Emergent OAuth
 * flow (same upstream provider, different exchange endpoint). The token
 * lives in `localStorage["ls.admin.token"]` and is sent on every admin
 * API request via the custom `Authorization: AdminBearer <token>`
 * scheme so the studio-side axios instance NEVER sends it.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";


const ADMIN_TOKEN_KEY = "ls.admin.token";
const ADMIN_BACKEND = process.env.REACT_APP_BACKEND_URL;


/** Dedicated axios instance for `/api/admin/*` calls. Does NOT inherit
 * the studio Bearer header. Pulls the admin token from localStorage on
 * every request via the request interceptor below. */
export const adminApi = axios.create({
  baseURL: `${ADMIN_BACKEND}/api`,
  withCredentials: true,
});

adminApi.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      // Custom scheme — distinct from studio `Bearer`. The backend's
      // `_parse_admin_token` only accepts this scheme.
      config.headers.Authorization = `AdminBearer ${token}`;
    }
  } catch (_) { /* SSR / private mode tolerance */ }
  return config;
});


const AdminAuthContext = createContext(null);


export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  // Module 6D Phase 7 — MFA challenge state. When non-null, the FE
  // routes through the TOTP challenge / enrollment screens (held by
  // the consumer of useAdminAuth). The pending token is stored ONLY
  // in component state, NEVER in localStorage — it's powerless and
  // ephemeral (5-min TTL on the server side).
  const [mfaChallenge, setMfaChallenge] = useState(null);
  // Same double-exchange guard pattern as studio auth — see lib/auth.jsx.
  const processedSessionIdRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      // 1. If the URL carries an OAuth `#session_id=…` hash (returning
      //    from `/admin/login` → Emergent OAuth), exchange it first.
      const hash = window.location.hash || "";
      const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const sessionId = params.get("session_id");
      if (sessionId && processedSessionIdRef.current !== sessionId) {
        processedSessionIdRef.current = sessionId;
        const { data } = await adminApi.post("/admin/auth/session", { session_id: sessionId });
        // Strip the hash so a refresh doesn't try to re-exchange.
        window.history.replaceState({}, "", window.location.pathname + window.location.search);
        if (data.stage === "mfa_required") {
          // Pending session — DO NOT persist anywhere. The MFA screen
          // owns the lifetime of this token (component state only).
          setMfaChallenge({
            pending_token:     data.pending_token,
            expires_in_seconds: data.expires_in_seconds,
            requires_enroll:   !!data.requires_enroll,
          });
          setAdmin(null);
          return;
        }
        // stage === "complete" — Phase A bootstrap window path.
        try { localStorage.setItem(ADMIN_TOKEN_KEY, data.token); } catch (_) {}
      }

      // 2. /me — works only if a token is present locally + admin
      //    session is alive server-side. Otherwise 401.
      if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
        setAdmin(null);
        return;
      }
      const { data } = await adminApi.get("/admin/auth/me");
      setAdmin(data.admin);
    } catch (err) {
      // 401, 403, network — admin is not logged in.
      setAdmin(null);
      try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch (_) {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await adminApi.post("/admin/auth/logout");
    } catch (_) {}
    try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch (_) {}
    setAdmin(null);
    setMfaChallenge(null);
  }, []);

  // Module 6D Phase 7 — Called by the MFA challenge screen after a
  // successful TOTP/backup code exchange. Stores the FULL admin token.
  const completeMfa = useCallback((adminPayload, token) => {
    try { localStorage.setItem(ADMIN_TOKEN_KEY, token); } catch (_) {}
    setMfaChallenge(null);
    setAdmin(adminPayload);
  }, []);

  return (
    <AdminAuthContext.Provider value={{
      admin, loading, checkAuth, logout,
      mfaChallenge, setMfaChallenge, completeMfa,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}


export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }
  return ctx;
}
